/*
 * DEPLOYMENT CANDIDATE — validate in an unpublished Shopify preview theme.
 *
 * Consent-aware first/last-touch attribution for the ZinVolt Shopify theme.
 * This module deliberately stores no email, phone, name or address and does
 * not push click IDs or attribution JSON into the dataLayer.
 */
(() => {
  'use strict';

  const GLOBAL_KEY = 'ZinVoltLeadAttribution';
  const META_LEAD_POLICY = window.ZinVoltMetaLeadPolicy || null;
  // Attribution and Meta-consent have deliberately separate schema versions.
  // Flow/Worker v2.1 expects attribution 1.0 and consent snapshot 1.1.
  const ATTRIBUTION_SCHEMA_VERSION = META_LEAD_POLICY?.attributionSchemaVersion || '1.0';
  const CONSENT_SCHEMA_VERSION = META_LEAD_POLICY?.consentSchemaVersion || '1.1';
  const CONSENT_POLICY_VERSION = META_LEAD_POLICY?.policyVersion || '';
  const ATTRIBUTION_FORM_IDS = new Set(['1101817', '1101824', '1101834']);
  const LOWERCASE_UUID_V4_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
  const LEGACY_ATTRIBUTION_SCHEMA_VERSIONS = new Set(['1.1']);
  const STORAGE_KEY = 'zv_lead_attribution_v1';
  const PENDING_SUBMISSION_PREFIX = 'zv_pending_submission_uuid_v1_';
  const ATTRIBUTION_TTL_MS = 90 * 24 * 60 * 60 * 1000;
  const PENDING_SUBMISSION_TTL_MS = 30 * 60 * 1000;
  const CONSENT_SNAPSHOT_REUSE_MS = 4 * 60 * 1000;
  const MAX_CAMPAIGN_VALUE_LENGTH = 256;
  const MAX_CLICK_ID_LENGTH = 512;

  if (window[GLOBAL_KEY]) return;

  const memoryPendingSubmissionIds = new Map();
  let memoryAttribution = null;

  const cleanText = (value, maxLength = MAX_CAMPAIGN_VALUE_LENGTH) => String(value || '')
    .replace(/[\u0000-\u001F\u007F]/g, '')
    .trim()
    .slice(0, maxLength);

  const cleanClickId = (value) => {
    const candidate = cleanText(value, MAX_CLICK_ID_LENGTH);
    return /^[A-Za-z0-9._~:+\-/=]+$/.test(candidate) ? candidate : '';
  };

  const safePageUrl = (value) => {
    try {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin) return '';
      return `${url.origin}${url.pathname}`.slice(0, 1024);
    } catch (error) {
      return '';
    }
  };

  const safePath = (value) => {
    try {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin) return '';
      return url.pathname.slice(0, 1024);
    } catch (error) {
      return '';
    }
  };

  const safeReferrer = (value) => {
    try {
      const url = new URL(value);
      if (!/^https?:$/.test(url.protocol)) return '';
      // Query strings and fragments can contain personal or sensitive data.
      return `${url.origin}${url.pathname}`.slice(0, 1024);
    } catch (error) {
      return '';
    }
  };

  const isExternalReferrer = (value) => {
    if (!value) return false;
    try {
      return new URL(value).origin !== window.location.origin;
    } catch (error) {
      return false;
    }
  };

  const readCurrentTouch = () => {
    const parameters = new URLSearchParams(window.location.search);
    const referrer = safeReferrer(document.referrer);
    return {
      schema_version: ATTRIBUTION_SCHEMA_VERSION,
      captured_at: new Date().toISOString(),
      landing_page: safePageUrl(window.location.href),
      path: safePath(window.location.href),
      referrer,
      external_referrer: isExternalReferrer(referrer),
      gclid: cleanClickId(parameters.get('gclid')),
      gbraid: cleanClickId(parameters.get('gbraid')),
      wbraid: cleanClickId(parameters.get('wbraid')),
      fbclid: cleanClickId(parameters.get('fbclid')),
      utm_source: cleanText(parameters.get('utm_source')),
      utm_medium: cleanText(parameters.get('utm_medium')),
      utm_campaign: cleanText(parameters.get('utm_campaign')),
      utm_id: cleanText(parameters.get('utm_id')),
      utm_term: cleanText(parameters.get('utm_term')),
      utm_content: cleanText(parameters.get('utm_content'))
    };
  };

  // Capture the arrival synchronously in memory. It is only persisted after
  // the Shopify CMP reports the relevant consent as granted.
  let pendingArrivalTouch = readCurrentTouch();

  const consentSnapshot = () => {
    const hasCmpState = Boolean(window.zvConsentState);
    const state = window.zvConsentState || {};
    const google = state.google || window.zvGoogleConsentDefault || {};
    return {
      schema_version: CONSENT_SCHEMA_VERSION,
      policy_version: CONSENT_POLICY_VERSION,
      captured_at: new Date().toISOString(),
      source: hasCmpState ? 'zinvolt_cmp' : 'google_consent_default',
      analytics_storage: google.analytics_storage === 'granted' ? 'granted' : 'denied',
      ad_storage: google.ad_storage === 'granted' ? 'granted' : 'denied',
      ad_user_data: google.ad_user_data === 'granted' ? 'granted' : 'denied',
      ad_personalization: google.ad_personalization === 'granted' ? 'granted' : 'denied',
      preferences: state.preferences === true ? 'granted' : 'denied',
      pending_sync: Boolean(state.pendingSync)
    };
  };

  const allowsAnalytics = (consent) => consent.analytics_storage === 'granted';
  const allowsAdAttribution = (consent) => (
    consent.ad_storage === 'granted' && consent.ad_user_data === 'granted'
  );

  const touchHasPersistableSignal = (touch) => Boolean(touch && (
    touch.landing_page
    || touch.path
    || touch.referrer
    || touch.gclid
    || touch.gbraid
    || touch.wbraid
    || touch.fbclid
    || touch.utm_source
    || touch.utm_medium
    || touch.utm_campaign
    || touch.utm_id
    || touch.utm_term
    || touch.utm_content
  ));

  const filterTouchForConsent = (touch, consent) => {
    if (!touch || (!allowsAnalytics(consent) && !allowsAdAttribution(consent))) return null;

    const filtered = {
      schema_version: ATTRIBUTION_SCHEMA_VERSION,
      captured_at: touch.captured_at
    };
    if (allowsAnalytics(consent)) {
      filtered.landing_page = touch.landing_page || '';
      filtered.path = touch.path || safePath(touch.landing_page) || '';
      filtered.referrer = touch.referrer || '';
      filtered.utm_source = touch.utm_source || '';
      filtered.utm_medium = touch.utm_medium || '';
      filtered.utm_campaign = touch.utm_campaign || '';
      filtered.utm_id = touch.utm_id || '';
      filtered.utm_term = touch.utm_term || '';
      filtered.utm_content = touch.utm_content || '';
    }
    if (allowsAdAttribution(consent)) {
      filtered.gclid = touch.gclid || '';
      filtered.gbraid = touch.gbraid || '';
      filtered.wbraid = touch.wbraid || '';
      filtered.fbclid = touch.fbclid || '';
    }
    return touchHasPersistableSignal(filtered) ? filtered : null;
  };

  const touchHasAcquisitionSignal = (touch) => Boolean(touch && (
    touch.gclid
    || touch.gbraid
    || touch.wbraid
    || touch.fbclid
    || touch.utm_source
    || touch.utm_medium
    || touch.utm_campaign
    || touch.utm_id
    || touch.utm_term
    || touch.utm_content
    || touch.external_referrer
    || (touch.referrer && isExternalReferrer(touch.referrer))
  ));

  const normaliseCapturedAt = (value) => {
    const parsed = new Date(value);
    return Number.isFinite(parsed.getTime()) ? parsed.toISOString() : new Date().toISOString();
  };

  const normaliseStoredTouch = (touch) => {
    if (!touch || typeof touch !== 'object') return null;
    const landingPage = safePageUrl(touch.landing_page);
    const normalised = {
      schema_version: ATTRIBUTION_SCHEMA_VERSION,
      captured_at: normaliseCapturedAt(touch.captured_at),
      landing_page: landingPage,
      path: safePath(touch.path || landingPage),
      referrer: safeReferrer(touch.referrer),
      utm_source: cleanText(touch.utm_source),
      utm_medium: cleanText(touch.utm_medium),
      utm_campaign: cleanText(touch.utm_campaign),
      utm_id: cleanText(touch.utm_id),
      utm_term: cleanText(touch.utm_term),
      utm_content: cleanText(touch.utm_content),
      gclid: cleanClickId(touch.gclid),
      gbraid: cleanClickId(touch.gbraid),
      wbraid: cleanClickId(touch.wbraid),
      fbclid: cleanClickId(touch.fbclid)
    };
    return touchHasPersistableSignal(normalised) ? normalised : null;
  };

  const readStoredAttribution = () => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null')
        || memoryAttribution;
      const supportedSchema = parsed?.schema_version === ATTRIBUTION_SCHEMA_VERSION
        || LEGACY_ATTRIBUTION_SCHEMA_VERSIONS.has(parsed?.schema_version);
      if (!parsed || !supportedSchema) return null;
      if (!Number.isFinite(parsed.expires_at) || parsed.expires_at <= Date.now()) {
        memoryAttribution = null;
        window.localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      memoryAttribution = {
        ...parsed,
        schema_version: ATTRIBUTION_SCHEMA_VERSION,
        first_touch: normaliseStoredTouch(parsed.first_touch),
        last_touch: normaliseStoredTouch(parsed.last_touch)
      };
      return memoryAttribution;
    } catch (error) {
      return memoryAttribution;
    }
  };

  const attributionAllowedByConsent = (record, consent) => {
    if (!record) return null;
    return {
      ...record,
      first_touch: filterTouchForConsent(record.first_touch, consent),
      last_touch: filterTouchForConsent(record.last_touch, consent)
    };
  };

  const writeStoredAttribution = (record) => {
    memoryAttribution = record?.first_touch || record?.last_touch ? record : null;
    try {
      if (!memoryAttribution) {
        window.localStorage.removeItem(STORAGE_KEY);
        return;
      }
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(memoryAttribution));
    } catch (error) {
      // Attribution is best-effort and must never block a form submission.
    }
  };

  const purgeDisallowedData = (consent) => {
    const stored = readStoredAttribution();
    if (!stored) return;
    const allowed = attributionAllowedByConsent(stored, consent);
    writeStoredAttribution({
      ...stored,
      first_touch: allowed.first_touch,
      last_touch: allowed.last_touch,
      updated_at: Date.now()
    });
  };

  const captureTouch = (rawTouch) => {
    const consent = consentSnapshot();
    const touch = filterTouchForConsent(rawTouch, consent);
    if (!touch) return attributionAllowedByConsent(readStoredAttribution(), consent);

    const stored = readStoredAttribution();
    const now = Date.now();
    const record = stored || {
      schema_version: ATTRIBUTION_SCHEMA_VERSION,
      created_at: now,
      expires_at: now + ATTRIBUTION_TTL_MS,
      first_touch: null,
      last_touch: null
    };

    // Always preserve the first eligible landing. Direct/internal navigation
    // never overwrites a prior campaign as the last touch.
    if (!record.first_touch) record.first_touch = touch;
    if (!record.last_touch || touchHasAcquisitionSignal(rawTouch)) record.last_touch = touch;
    record.updated_at = now;
    record.expires_at = now + ATTRIBUTION_TTL_MS;
    writeStoredAttribution(record);
    return attributionAllowedByConsent(record, consent);
  };

  const captureCurrentTouch = () => captureTouch(readCurrentTouch());

  const pendingSubmissionKey = (formId) => `${PENDING_SUBMISSION_PREFIX}${String(formId || 'unknown')}`;

  const createFirstPartySubmissionUuid = () => {
    try {
      const uuid = window.crypto?.randomUUID?.();
      if (LOWERCASE_UUID_V4_PATTERN.test(uuid || '')) return uuid;
    } catch (error) {
      // Continue only to the Web Crypto getRandomValues implementation.
    }
    try {
      if (!window.crypto?.getRandomValues) return '';
      const bytes = window.crypto.getRandomValues(new Uint8Array(16));
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'));
      const uuid = `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10).join('')}`;
      return LOWERCASE_UUID_V4_PATTERN.test(uuid) ? uuid : '';
    } catch (error) {
      return '';
    }
  };

  const createDistinctConsentSnapshotId = (leadUuid) => {
    // A broken/non-random Web Crypto implementation must fail closed instead
    // of reusing the lead identity as a consent-audit identity.
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const candidate = createFirstPartySubmissionUuid();
      if (LOWERCASE_UUID_V4_PATTERN.test(candidate) && candidate !== leadUuid) return candidate;
    }
    return '';
  };

  const consentSnapshotFingerprint = (snapshot) => JSON.stringify({
    schema_version: snapshot?.schema_version || '',
    policy_version: snapshot?.policy_version || '',
    source: snapshot?.source || '',
    analytics_storage: snapshot?.analytics_storage || 'denied',
    ad_storage: snapshot?.ad_storage || 'denied',
    ad_user_data: snapshot?.ad_user_data || 'denied',
    ad_personalization: snapshot?.ad_personalization || 'denied',
    preferences: snapshot?.preferences || 'denied',
    pending_sync: Boolean(snapshot?.pending_sync)
  });

  const isReusableConsentSnapshot = (pending, currentConsent) => {
    if (!LOWERCASE_UUID_V4_PATTERN.test(pending?.consent_snapshot_id || '')) return false;
    if (!pending?.consent_snapshot || typeof pending.consent_snapshot !== 'object') return false;
    const capturedAt = Date.parse(pending.consent_snapshot.captured_at || '');
    if (!Number.isFinite(capturedAt) || Date.now() - capturedAt >= CONSENT_SNAPSHOT_REUSE_MS) return false;
    return consentSnapshotFingerprint(pending.consent_snapshot) === consentSnapshotFingerprint(currentConsent);
  };

  const copyConsentSnapshot = (snapshot) => ({
    schema_version: snapshot?.schema_version || CONSENT_SCHEMA_VERSION,
    policy_version: snapshot?.policy_version || CONSENT_POLICY_VERSION,
    captured_at: snapshot?.captured_at || new Date().toISOString(),
    source: snapshot?.source || 'google_consent_default',
    analytics_storage: snapshot?.analytics_storage === 'granted' ? 'granted' : 'denied',
    ad_storage: snapshot?.ad_storage === 'granted' ? 'granted' : 'denied',
    ad_user_data: snapshot?.ad_user_data === 'granted' ? 'granted' : 'denied',
    ad_personalization: snapshot?.ad_personalization === 'granted' ? 'granted' : 'denied',
    preferences: snapshot?.preferences === 'granted' ? 'granted' : 'denied',
    pending_sync: Boolean(snapshot?.pending_sync)
  });

  const getOrCreateSubmissionIdentity = (formId, currentConsent) => {
    // The browser-generated UUID becomes the canonical lead UUID for a new
    // Salesdock opportunity. A second, independent UUID identifies the exact
    // immutable consent snapshot captured for that submission. Both values are
    // kept stable across an ambiguous timeout/retry within this session.
    if (!ATTRIBUTION_FORM_IDS.has(String(formId || ''))) {
      return { lead_uuid: '', consent_snapshot_id: '' };
    }

    const key = pendingSubmissionKey(formId);
    let pending = null;
    try {
      pending = JSON.parse(window.sessionStorage.getItem(key) || 'null')
        || memoryPendingSubmissionIds.get(key)
        || null;
    } catch (error) {
      pending = memoryPendingSubmissionIds.get(key) || null;
    }

    if (
      LOWERCASE_UUID_V4_PATTERN.test(pending?.uuid || '')
      && Date.now() - Number(pending.created_at || 0) < PENDING_SUBMISSION_TTL_MS
    ) {
      // Reuse the exact immutable snapshot only while consent semantics remain
      // unchanged and the snapshot stays inside the downstream freshness
      // window. A consent change or stale snapshot gets a new audit UUID.
      if (!isReusableConsentSnapshot(pending, currentConsent)) {
        pending.consent_snapshot_id = createDistinctConsentSnapshotId(pending.uuid);
        pending.consent_snapshot = copyConsentSnapshot(currentConsent);
        memoryPendingSubmissionIds.set(key, pending);
        try {
          window.sessionStorage.setItem(key, JSON.stringify(pending));
        } catch (error) {
          // The in-memory copy still keeps the identity stable for this view.
        }
      }
      return {
        lead_uuid: pending.uuid,
        consent_snapshot_id: LOWERCASE_UUID_V4_PATTERN.test(pending.consent_snapshot_id || '')
          ? pending.consent_snapshot_id
          : '',
        consent_snapshot: pending.consent_snapshot
      };
    }

    const uuid = createFirstPartySubmissionUuid();
    const consentSnapshotId = createDistinctConsentSnapshotId(uuid);
    if (!LOWERCASE_UUID_V4_PATTERN.test(uuid)) {
      return { lead_uuid: '', consent_snapshot_id: '' };
    }
    pending = {
      uuid,
      consent_snapshot_id: LOWERCASE_UUID_V4_PATTERN.test(consentSnapshotId) ? consentSnapshotId : '',
      consent_snapshot: copyConsentSnapshot(currentConsent),
      created_at: Date.now()
    };
    memoryPendingSubmissionIds.set(key, pending);
    try {
      window.sessionStorage.setItem(key, JSON.stringify(pending));
    } catch (error) {
      // The in-memory copy still prevents a new UUID during this page view.
    }
    return {
      lead_uuid: pending.uuid,
      consent_snapshot_id: pending.consent_snapshot_id,
      consent_snapshot: pending.consent_snapshot
    };
  };

  const markSubmissionSuccess = (formId, submissionUuid) => {
    const key = pendingSubmissionKey(formId);
    const current = memoryPendingSubmissionIds.get(key);
    if (!current || current.uuid === submissionUuid) memoryPendingSubmissionIds.delete(key);
    try {
      const stored = JSON.parse(window.sessionStorage.getItem(key) || 'null');
      if (!stored || stored.uuid === submissionUuid) window.sessionStorage.removeItem(key);
    } catch (error) {
      // No action is required when sessionStorage is unavailable.
    }
  };

  const safeJson = (value) => {
    try {
      return JSON.stringify(value || null);
    } catch (error) {
      return 'null';
    }
  };

  const preferredGoogleClickId = (attribution, consent) => {
    if (!allowsAdAttribution(consent)) return { type: '', value: '' };
    // Preserve the live priority contract: newest touch before first touch,
    // then gclid, wbraid and gbraid within the selected touch.
    for (const touch of [attribution?.last_touch, attribution?.first_touch]) {
      if (touch?.gclid) return { type: 'gclid', value: touch.gclid };
      if (touch?.wbraid) return { type: 'wbraid', value: touch.wbraid };
      if (touch?.gbraid) return { type: 'gbraid', value: touch.gbraid };
    }
    return { type: '', value: '' };
  };

  const createLeadContext = (formId) => {
    captureCurrentTouch();
    const currentConsent = consentSnapshot();
    // Stored attribution may predate a consent withdrawal. Never expose it to
    // the bridge until the current consent snapshot allows the relevant data.
    const submissionIdentity = getOrCreateSubmissionIdentity(formId, currentConsent);
    const consent = submissionIdentity.consent_snapshot || currentConsent;
    const attribution = attributionAllowedByConsent(readStoredAttribution(), consent);
    const clickId = preferredGoogleClickId(attribution, consent);
    const leadUuid = submissionIdentity.lead_uuid;
    return {
      schema_version: ATTRIBUTION_SCHEMA_VERSION,
      // The source-generated UUID is canonical for a newly-created Salesdock
      // opportunity. submission_uuid remains as a compatibility alias for the
      // existing Meta/Shopify idempotency path; Shopify system.id stays a
      // separate source-record identifier and must never replace this UUID.
      submission_uuid: leadUuid,
      provisional_lead_uuid: leadUuid,
      lead_uuid: leadUuid,
      lead_uuid_status: leadUuid ? 'canonical' : 'unavailable',
      consent_snapshot_id: submissionIdentity.consent_snapshot_id,
      // Raw objects are only for first-party bridge logic. Persist/send the
      // serialized variants below; never map these objects into GA4.
      first_touch: attribution?.first_touch || null,
      last_touch: attribution?.last_touch || null,
      consent_snapshot: consent,
      first_touch_json: safeJson(attribution?.first_touch || null),
      last_touch_json: safeJson(attribution?.last_touch || null),
      google_click_id_type: clickId.type,
      google_click_id: clickId.value,
      consent_snapshot_json: safeJson(consent)
    };
  };

  const clearAttribution = () => {
    pendingArrivalTouch = null;
    memoryAttribution = null;
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      // Nothing else to clear.
    }
  };

  const capturePendingArrivalForKnownConsent = () => {
    const consent = consentSnapshot();
    if (!allowsAnalytics(consent) && !allowsAdAttribution(consent)) return;
    if (pendingArrivalTouch) captureTouch(pendingArrivalTouch);
    const currentTouch = readCurrentTouch();
    captureTouch(currentTouch);
    pendingArrivalTouch = filterTouchForConsent(currentTouch, consent);
  };

  document.addEventListener('zv:cookie-consent-updated', () => {
    const consent = consentSnapshot();
    purgeDisallowedData(consent);
    if (allowsAnalytics(consent) || allowsAdAttribution(consent)) {
      capturePendingArrivalForKnownConsent();
    } else {
      pendingArrivalTouch = null;
    }
  });

  // Existing consent is restored by the CMP during DOMContentLoaded. Queueing
  // this capture one task later closes the gap in which a visitor could leave
  // the campaign landing before Shopify's asynchronous privacy readback emits
  // its own consent event.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.setTimeout(capturePendingArrivalForKnownConsent, 0);
    }, { once: true });
  } else {
    window.setTimeout(capturePendingArrivalForKnownConsent, 0);
  }

  // A page restored from the back-forward cache does not rerun this module.
  // Re-evaluate the current URL without allowing direct/internal navigation to
  // replace a known acquisition touch.
  window.addEventListener('pageshow', capturePendingArrivalForKnownConsent);
  window.addEventListener('popstate', capturePendingArrivalForKnownConsent);

  window[GLOBAL_KEY] = {
    schemaVersion: ATTRIBUTION_SCHEMA_VERSION,
    captureCurrentTouch,
    consentSnapshot,
    createLeadContext,
    markSubmissionSuccess,
    clearAttribution
  };
})();
